import { createSlice } from '@reduxjs/toolkit';
import {
  GetStatsByPaymentMethodResponse,
  GetStatsByPaymentMethodDetailResponse,
  PaymentMethodBucket,
  PaymentMethodTotalsEntry,
  StatisticByPaymentMethodEvent,
} from 'src/models/statistic';

interface StatisticByPaymentMethodState {
  isLoading: boolean;
  isLoadingDetails: boolean;
  isErrorMessage: string;
  statusCode: number;
  totals: Record<PaymentMethodBucket, PaymentMethodTotalsEntry>;
  statisticsByPaymentMethod: StatisticByPaymentMethodEvent[];
  statisticsByPaymentMethodPaginated: StatisticByPaymentMethodEvent[];
  pagePm: number;
  limitPm: number;
  total: number;
  changeStsPaymentMethod: null;
  filters: any;
  details: GetStatsByPaymentMethodDetailResponse;
}

const emptyTotals = (): Record<PaymentMethodBucket, PaymentMethodTotalsEntry> => ({
  qr: { qt: 0, totalProducer: 0 },
  cash: { qt: 0, totalProducer: 0 },
  payment_link: { qt: 0, totalProducer: 0 },
  other: { qt: 0, totalProducer: 0 },
});

const initialState: StatisticByPaymentMethodState = {
  isLoading: false,
  isLoadingDetails: false,
  statusCode: 201,
  isErrorMessage: '',
  totals: emptyTotals(),
  statisticsByPaymentMethod: [],
  statisticsByPaymentMethodPaginated: [],
  pagePm: 0,
  limitPm: 25,
  total: 0,
  changeStsPaymentMethod: null,
  filters: {
    filter: {
      tktDates: {
        from: '',
        to: '',
      },
      place: '',
      state: '',
      onlyGift: false,
      event: '',
      timeZone: '',
    },
    paymentMethod: null,
    order: {
      orderBy: 'evtdatetime',
      orderDesc: true,
    },
  },
  details: {
    results: [],
    availableMethods: [],
  },
};

export const statisticByPaymentMethodSlice = createSlice({
  name: 'statisticByPaymentMethod',
  initialState,
  reducers: {
    startLoadingStatisticsByPaymentMethod: (state) => {
      state.isLoading = true;
    },
    setStatisticsByPaymentMethod: (state, action: { payload: GetStatsByPaymentMethodResponse }) => {
      state.isLoading = false;
      state.statisticsByPaymentMethod = action.payload.results ?? [];
    },
    setStatisticsByPaymentMethodPaginated: (
      state,
      action: { payload: GetStatsByPaymentMethodResponse },
    ) => {
      state.isLoading = false;
      state.totals = action.payload.totals ?? emptyTotals();
      state.statisticsByPaymentMethodPaginated = action.payload.results ?? [];
      state.pagePm = action.payload.page ?? 1;
      state.limitPm = action.payload.limit ?? 25;
      state.total = Number(action.payload.total) || 0;
    },
    setchangeStsPaymentMethod: (state, action) => {
      state.changeStsPaymentMethod = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    startLoadingDetails: (state) => {
      state.isLoadingDetails = true;
    },
    setDetails: (state, action: { payload: GetStatsByPaymentMethodDetailResponse }) => {
      state.isLoadingDetails = false;
      state.details = action.payload ?? { results: [], availableMethods: [] };
    },
    clearDetails: (state) => {
      state.details = { results: [], availableMethods: [] };
    },
    stopLoadingStatisticsByPaymentMethod: (state) => {
      state.isLoading = false;
    },
    stopLoadingDetails: (state) => {
      state.isLoadingDetails = false;
    },
    setError: (state, action) => {
      const message = action.payload.message;
      state.statusCode = action.payload.status;
      state.isErrorMessage = message;
    },
  },
});

export const {
  startLoadingStatisticsByPaymentMethod,
  setStatisticsByPaymentMethod,
  setStatisticsByPaymentMethodPaginated,
  setchangeStsPaymentMethod,
  setFilters,
  setDetails,
  clearDetails,
  stopLoadingStatisticsByPaymentMethod,
  startLoadingDetails,
  stopLoadingDetails,
  setError,
} = statisticByPaymentMethodSlice.actions;
